/**
 * The list tables (admin medicines, doctors, staff and status master; support request analytics):
 * a frame that keeps the page's search and filters in view, because only its rows scroll.
 *
 * Height: the page is a full-height column (`flex h-full flex-col`) with the table last; the table
 * takes the height left under the title and filters. Its scroller is placed absolutely in that
 * slot, so the rows never count towards the page's height and a long list can't stretch the page
 * (RESP-27: the whole page scrolled and the filters went out of view). It keeps at least 16rem; on
 * a screen too short even for that, the page scrolls as any page does.
 *
 * Width: columns are fixed grid tracks that step with the screen (lib/tableColumns.ts): a phone
 * gets the columns that matter at widths that fit, wider screens get the rest. Were the tracks
 * shown still wider than the frame, the table scrolls sideways inside it, header and rows together.
 *
 * Rows are virtual: only those in view, and a few either side, are drawn, and the next page is
 * asked for a few rows before the end. The column header sticks to the top of the rows. Rows have
 * a fixed height, or are measured one by one (`measureRows`) where they wrap or expand.
 */

import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";

import { cellClass, columnTemplates, TABLE_GRID } from "../../lib/tableColumns";
import type { TableColumn } from "../../lib/tableColumns";

export function VirtualTable<T>({
  label,
  columns,
  rows,
  rowKey,
  rowHeight,
  measureRows = false,
  renderRow,
  loading = false,
  error = null,
  onRetry,
  empty,
  toolbar,
  total,
  totalLabel,
  hasMore = false,
  loadingMore = false,
  loadMoreFailed = false,
  onLoadMore,
  resetKey,
}: {
  /** The table's accessible name. */
  label: string;
  columns: TableColumn[];
  rows: T[];
  rowKey: (row: T) => string;
  /** A row's height in px — exact, or the estimate before measuring when `measureRows`. */
  rowHeight: number;
  measureRows?: boolean;
  /** One row's content: usually a <TableRow> of the same columns. */
  renderRow: (row: T, index: number) => ReactNode;
  /** The first page is loading. */
  loading?: boolean;
  /** Why the rows couldn't be loaded. */
  error?: ReactNode;
  onRetry?: () => void;
  /** Said when there are no rows. */
  empty: ReactNode;
  /** Above the column header and fixed there, e.g. bulk actions for selected rows. */
  toolbar?: ReactNode;
  /** Rows matching, loaded or not (defaults to the rows given). */
  total?: number;
  /** Said in place of `total` when the count isn't exact, e.g. "10,000+" for a capped count. */
  totalLabel?: string;
  hasMore?: boolean;
  loadingMore?: boolean;
  loadMoreFailed?: boolean;
  /** Fetches the next page; pass a stable function (useInfiniteQuery's fetchNextPage). */
  onLoadMore?: () => unknown;
  /** A change (a new search or filter) scrolls back to the top. */
  resetKey?: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  // The rows start under the sticky column header, inside the same scroller.
  const [headerHeight, setHeaderHeight] = useState(0);
  useLayoutEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const measure = () => setHeaderHeight(header.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    getItemKey: (i) => rowKey(rows[i]),
    overscan: 8,
    scrollMargin: headerHeight,
  });
  const items = virtualizer.getVirtualItems();

  // A new search or filter starts at the top of its results. Declared before the next-page check
  // below, so that check already sees the top.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [resetKey]);

  // Ask for the next page a few rows before the end, so scrolling rarely waits. Measured on the
  // scroller: in the render right after a new search the virtualiser can still hold the previous
  // results' position, and asked for a page nobody had scrolled to.
  const lastIndex = items.length ? items[items.length - 1].index : -1;
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller || !onLoadMore || !hasMore || loadingMore || loadMoreFailed || rows.length === 0) return;
    if (scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < rowHeight * 5) void onLoadMore();
  }, [lastIndex, rows.length, hasMore, loadingMore, loadMoreFailed, onLoadMore, rowHeight]);

  let body: ReactNode;
  if (loading) {
    body = (
      <div className="space-y-2 p-4" aria-hidden>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-8 animate-pulse rounded-sm bg-gray-100" />
        ))}
      </div>
    );
  } else if (error && rows.length === 0) {
    body = (
      <Centered>
        <span className="text-red-600">{error}</span>
        {onRetry && (
          <button type="button" onClick={onRetry} className="font-medium text-brand hover:underline">
            Try again
          </button>
        )}
      </Centered>
    );
  } else if (rows.length === 0) {
    body = (
      <Centered>
        <span className="text-2xl" aria-hidden>
          ∅
        </span>
        <span>{empty}</span>
      </Centered>
    );
  } else {
    body = (
      <div role="rowgroup" className="relative" style={{ height: virtualizer.getTotalSize() }}>
        {items.map((vi) => (
          <div
            key={vi.key}
            data-index={vi.index}
            ref={measureRows ? virtualizer.measureElement : undefined}
            className="absolute left-0 top-0 w-full min-w-min border-b"
            style={{
              transform: `translateY(${vi.start - virtualizer.options.scrollMargin}px)`,
              height: measureRows ? undefined : vi.size,
            }}
          >
            {renderRow(rows[vi.index], vi.index)}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className="flex min-h-64 flex-1 flex-col overflow-hidden rounded-xl border bg-white shadow-xs"
      style={columnTemplates(columns) as CSSProperties}
    >
      {toolbar}
      <div className="relative min-h-0 flex-1">
        <div ref={scrollRef} className="absolute inset-0 overflow-auto" aria-busy={loading}>
          <div role="table" aria-label={label}>
            <div role="rowgroup" ref={headerRef} className="sticky top-0 z-10 min-w-min border-b bg-gray-50">
              <div role="row" className={`${TABLE_GRID} px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-gray-500`}>
                {columns.map((c) => (
                  <div key={c.key} role="columnheader" className={`${cellClass(c)} whitespace-nowrap`}>
                    {c.header}
                  </div>
                ))}
              </div>
            </div>
            {body}
          </div>
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-between gap-3 border-t px-4 py-2 text-xs text-gray-500" aria-live="polite">
        <span>{loading ? "" : `Showing ${rows.length} of ${totalLabel ?? total ?? rows.length}`}</span>
        {loadingMore ? (
          <span className="animate-pulse">Loading more…</span>
        ) : loadMoreFailed && onLoadMore ? (
          <button type="button" onClick={() => void onLoadMore()} className="font-medium text-red-600 hover:underline">
            Couldn't load more — try again
          </button>
        ) : hasMore ? (
          <span>Scroll to load more</span>
        ) : (
          onLoadMore && rows.length > 0 && <span>End of list</span>
        )}
      </div>
    </div>
  );
}

/** One table line: a cell per column, in column order; columns not shown at this width drop out. */
export function TableRow({
  columns,
  cells,
  className = "",
}: {
  columns: TableColumn[];
  cells: Record<string, ReactNode>;
  className?: string;
}) {
  return (
    <div role="row" className={`${TABLE_GRID} px-4 ${className}`}>
      {columns.map((c) => (
        <div key={c.key} role="cell" className={cellClass(c)}>
          {cells[c.key]}
        </div>
      ))}
    </div>
  );
}

function Centered({ children }: { children: ReactNode }) {
  return <div className="flex flex-col items-center justify-center gap-1 px-4 py-12 text-center text-sm text-gray-400">{children}</div>;
}
